import { type ComponentProps, type FormEvent, useState } from 'react';

import { AuthAside } from '@/components/auth-aside';
import { GoogleSignInButton } from '@/components/google-sign-in-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { supabase } from '../lib/supabase.ts';

interface LoginFormProps extends ComponentProps<'div'> {
  /** Hands the screen over to the sign-up form. */
  onSwitchToSignup: () => void;
}

/**
 * shadcn's login-04 block, wired to Supabase password auth.
 *
 * Of the block's Apple/Google/Meta row only Google survives: it is the one
 * provider `supabase/config.toml` enables, and a button for a disabled provider
 * could only ever fail. Add another back alongside its provider, not ahead of
 * it.
 *
 * Nothing here is a security boundary — a successful sign-in only produces a
 * token that RLS and the admin API then judge on their own terms.
 */
export function LoginForm({ className, onSwitchToSignup, ...props }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setIsSubmitting(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

    // On success the auth listener in AuthProvider swaps this screen for the
    // console, so there is nothing to do but surface a failure.
    if (signInError) {
      setError(signInError.message);
    }

    setIsSubmitting(false);
  }

  async function handleForgotPassword() {
    setError(null);
    setNotice(null);

    if (!email) {
      setError('Enter your email address first, then request a reset link.');
      return;
    }

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });

    if (resetError) {
      setError(resetError.message);
      return;
    }

    setNotice(`If ${email} has an account, a reset link is on its way.`);
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Welcome back</h1>
                <p className="text-muted-foreground text-balance">
                  Sign in to the ctrl operator console
                </p>
              </div>

              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="ml-auto text-sm underline-offset-2 hover:underline"
                  >
                    Forgot your password?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              {error && <FieldError>{error}</FieldError>}
              {notice && <FieldDescription>{notice}</FieldDescription>}

              <Field>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </Field>

              <GoogleSignInButton label="Sign in" onError={setError} />

              <FieldDescription className="text-center">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={onSwitchToSignup}
                  className="underline underline-offset-4"
                >
                  Sign up
                </button>
              </FieldDescription>
            </FieldGroup>
          </form>

          <AuthAside />
        </CardContent>
      </Card>
    </div>
  );
}
